export interface Dep {
  name: string;
  branch: string;
  deployTag: string;
  // `type` removed; we infer infra vs service by deployTag === branch
}

export interface Release {
  name: string;
  dependencies?: { name: string; required?: boolean }[];
}

export type Metadata = { releases: Release[] };

const SUPER_DEPS = ['shared-defaults', 'shared-config', 'shared-storage'];

interface Entry {
  entry: Record<string, string>;
  type: 'infrastructure' | 'service';
}

/**
 * Build map from app name to entry and inferred type
 */
function makeFilterMap(deps: Dep[]): Record<string, Entry> {
  return deps.reduce((m, d) => {
    const isInfra = d.deployTag === d.branch;
    m[d.name] = {
      entry: { [d.name]: d.deployTag },
      type: isInfra ? 'infrastructure' : 'service'
    };
    return m;
  }, {} as Record<string, Entry>);
}

/**
 * Topological layering based on dependencies & filter map
 */
function topoGroups(meta: Metadata, fm: Record<string, Entry>): string[][] {
  const apps: Record<string, string[]> = {};
  const order: string[] = [];
  const seen = new Set<string>();

  for (const r of meta.releases) {
    if (!fm[r.name]) continue;
    seen.add(r.name);
    order.push(r.name);
    apps[r.name] = (r.dependencies || [])
      .filter(d => d.required !== false && fm[d.name])
      .map(d => d.name);
  }

  for (const name of Object.keys(fm)) {
    if (!seen.has(name)) {
      order.push(name);
      apps[name] = [];
    }
  }

  const groups: string[][] = [];
  const done = new Set<string>();
  while (done.size < order.length) {
    const layer = order.filter(n => !done.has(n) && apps[n].every(dep => done.has(dep)));
    if (!layer.length) {
      order.filter(n => !done.has(n)).forEach(n => layer.push(n));
    }
    layer.forEach(n => done.add(n));
    groups.push(layer);
  }
  return groups;
}

export function buildDeploymentGroups(
  meta: Metadata,
  deps: Dep[]
): Array<Record<string,string>[]> {
  const fm = makeFilterMap(deps);

  // 1) super‐group
  const superGroup = SUPER_DEPS
    .filter(name => fm[name])
    .map(name => fm[name].entry);

  // 2) infra vs service maps
  const fmInfra = Object.fromEntries(
    Object.entries(fm)
      .filter(([k,e]) => e.type === 'infrastructure' && !SUPER_DEPS.includes(k))
  ) as Record<string,Entry>;

  const fmSvc = Object.fromEntries(
    Object.entries(fm)
      .filter(([k,e]) => e.type === 'service' && !SUPER_DEPS.includes(k))
  ) as Record<string,Entry>;

  // 3) build sub‐metadatas
  const infraMeta = { releases: meta.releases.filter(r => !!fmInfra[r.name]) };
  const svcMeta   = { releases: meta.releases.filter(r => !!fmSvc  [r.name]) };

  // 4) topoGroups for infra and for services
  const infraGroups = topoGroups(infraMeta, fmInfra)
    .map(layer => layer.map(name => fmInfra[name].entry));

  const svcGroups = topoGroups(svcMeta, fmSvc)
    .map(layer => layer.map(name => fmSvc[name].entry));

  // 5) stitch them all together
  return [
    ...(superGroup.length ? [superGroup] : []),
    ...infraGroups,
    ...svcGroups
  ];
}
